import React from 'react';
import axios from 'axios';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import SystemErrorIcon from './SystemErrorIcon';
import Select from 'react-select';

import {
	buildTestData,
} from 'Vulture/TestData';

import style from './Systems.module.css';
import SystemsModal from './SystemsModal.js';
import SystemsDetailModal from './SystemsDetailModal.js';
import PWACopyURLButton from './Elements/PWACopyURLButton';
import { getPWAURL, PWA_NO_COMPONENT_TEXT } from './utils/pwa';

import {
	dlImagePreviewPush,
} from 'Vulture/DataLayer';

import {
	getFDValue,
} from 'Vulture/Helpers';

import {
	hasGlobalScope,
	hasPermission,
	isPropertyScoped,
} from 'Vulture/Permissions.js';

import {
	vultureENV,
	broncoURL,
} from 'Vulture/ENV';

import {
	getSession,
} from 'Vulture/Session';

import BroncoSDK from 'Vulture/lib/BroncoSDK.js';
import HummingbirdSDK from 'Vulture/lib/HummingbirdSDK.js';

const broncoSDK = new BroncoSDK({
	baseURL: broncoURL,
});

const hummingbirdSDK = new HummingbirdSDK({
	baseURL: window._getEnv('HUMMINGBIRD_URL'),
});

import SystemsHierarchyTable from './SystemsHierarchyTable';

import {
	combineSubrows,
	getFilteredHierarchy,
} from 'Vulture/Systems';
import Img from 'wrapper/Img';
import { s3PublicURL } from 'Vulture/S3.js';

class SystemsView extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			// DEV-12007 Import Users modal: null, or
			// { phase: 'loading'|'confirm'|'submitting'|'results'|'error', uuid, displayName, preview?, results?, error? }
			importModal:            null,
			accountDropdownEnabled: false,
			accountDropdownJson:    {},
			condorThemeID:          '',
			systemRows:             [],
			orientationID:          '',
			renderObj:              {},
			systemsTableData:       [],
			resolutionID:           '',
			systemState:            '',
			topLevelSystemGroupID:  null,
			nameFilter:             '',
			statusFilter:           '',
			resolutionFilter:       '',
			macAddressFilter:       '',
			orientationFilter:      '',
			directoryNumberFilter:  '',
			projectNumberFilter:    '',
			systemUUIDs:            [],
			systemStatusInfo:       {},
			systemsAboutToExpire:   [],

			// Column widths -- should add up to 100
			systemNameColumn: {
				'flexGrow': '1',
			},
			statusColumn: {
				'width': '10em',
			},
			resolutionColumn: {
				'width': '10em',
			},
			macAddressColumn: {
				'width': '10em',
			},
			orientationColumn: {
				'width': '10em',
			},
			directoryNumberColumn: {
				'width': '10em',
			},
			componentGroupColumn: {
				'display': 'none',
			},
			themeColumn: {
				'display': 'none',
			},
		};

		this._hummingbirdTimer = null;
		this._lastMacAddresses = null;
		this._makeTableTimer = null;
	}

	componentDidMount() {
		this.queryHummmingbird();

		let condorThemeID;
		let	orientationID;
		let	resolutionID;

		this.setAnimPhaseDropdowns();

		this.setState({
			condorThemeID,
			orientationID,
			resolutionID,
		}, () => {
			this.makeTable();
		});

	}

	shouldComponentUpdate(nextProps, nextState) {
		// Skip re-rendering the entire Systems table while a modal is open —
		// the table is behind the modal and will re-render when it closes.
		if (this.props.app.state.modal) {
			return this.state !== nextState;
		}
		return true;
	}

	componentDidUpdate(prevProps) {
		const {
			fd,
			systemGroupUUID,
			roleUUID,
		} = this.props;

		// Hierarchy filtering reads the resolved permissions (hasGlobalScope /
		// isPropertyScoped) at table-build time. On an IN-PLACE account switch (header
		// dropdown) the component is reused, not remounted, so rebuild when the resolved
		// role for the selected account changes — otherwise the previous account's role
		// (e.g. external_admin) keeps showing all system groups on an account where the
		// user is limited. Also resolves the fd-vs-permissions fetch race (whichever lands
		// last triggers a correct rebuild). Keyed on role UUID, not display name: custom
		// roles on different accounts may share a name.
		if (roleUUID && roleUUID !== prevProps.roleUUID && !this.props.app.state.modal) {
			this.debouncedMakeTable();
		}

		if (prevProps.fd !== fd) {
			// Skip expensive table rebuilds while a modal is open — the table
			// is behind the modal and will rebuild when the modal closes.
			if (this.props.app.state.modal) {
				return;
			}

			this.setState({
				accountDropdownJson:    [],
				accountDropdownEnabled: false,
			});
			this.setAnimPhaseDropdowns();
			this.debouncedMakeTable();
			this.debouncedQueryHummingbird();
		}

		if (prevProps.systemGroupUUID !== systemGroupUUID) {
			if (systemGroupUUID) {
				this.expandAllRows();
			} else {
				this.collapseAllRows();
			}
		}
	}

	componentWillUnmount() {
		clearTimeout(this._hummingbirdTimer);
		clearTimeout(this._makeTableTimer);
	}

	debouncedQueryHummingbird() {
		clearTimeout(this._hummingbirdTimer);
		this._hummingbirdTimer = setTimeout(() => this.queryHummmingbird(), 500);
	}

	debouncedMakeTable() {
		clearTimeout(this._makeTableTimer);
		this._makeTableTimer = setTimeout(() => this.makeTable(), 100);
	}

	setAnimPhaseDropdowns() {
		const {
			fd,
		} = this.props;

		Object.keys(fd).forEach((m1Key) => {
			if (m1Key.startsWith('account.')) {
				this.setState({
					accountDropdownEnabled: fd[m1Key].animation_phase_toggle,
					accountDropdownJson:    (() => { try { return JSON.parse(fd[m1Key].animation_phase_options || '{}'); } catch (e) { return { options: [] }; } })(),
				}, () => {
					this.makeTable();
				});
			}
		});
	}

	makeTable() {
		const {
			accountUUID,
			app,
		} = this.props;

		const systemsTableData = this.findInitialSystemGroupsForTable(accountUUID);
		const sortedTableData = Object.values(systemsTableData).sort(this.sortByName);

		this.setState({
			systemsTableData: sortedTableData,
		});

		const diffDays = this.getEarliestExpiration();
		app.setState({
			nearestSystemExpiration: diffDays,
		});
	}

	extractMacAddresses(fd) {
		const macAddresses = [];
		for (const [ m1Key, m1Data, ] of Object.entries(fd)) {
			if (m1Key.startsWith('system.')) {
				for (const [ m2Key, m2Data, ] of Object.entries(m1Data)) {
					if (m2Key === 'mac_address') {
						macAddresses.push(m2Data);
					}
				}
			}
		}
		return macAddresses.sort();
	}

	async queryHummmingbird() {
		let systemStatusInfo = {};
		const macAddresses = this.extractMacAddresses(this.props.fd);

		if (macAddresses.length === 0) {
			return;
		}

		// Skip API call if MAC addresses haven't changed since last query
		const macKey = macAddresses.sort().join(',');
		if (macKey === this._lastMacAddresses) {
			return;
		}
		this._lastMacAddresses = macKey;

		try {
			const contents = await hummingbirdSDK.listDevices(macAddresses);

			if (contents && contents.data) {
				contents.data.forEach((object) => {
					if (object.agent?.tag) {
						systemStatusInfo[object.agent.tag] = {
							conn: object.conn,
							os:   object.osdesc,
							ip:   object.ip,
						};
					}
				});
			}
			this.setState((prevState) => ({
				systemStatusInfo,
				systemsTableData: this.patchStatusInTable(prevState.systemsTableData, systemStatusInfo),
			}));
		} catch (err) {
			console.error(err);
		}
	}

	patchStatusInTable(rows, systemStatusInfo) {
		return rows.map((row) => {
			const updated = { ...row };
			// PWA rows keep their PWA status; they aren't Hummingbird devices
			if (updated.macAddress && !updated.isPWA) {
				let statusText = 'Not Connected';
				let statusJSX = <span className="errorIcon"><CustomIcon icon="exclamation-circle" /> {statusText}</span>;

				if (systemStatusInfo[updated.macAddress]?.conn === 1) {
					statusText = 'Online';
					statusJSX = <span><CustomIcon icon="check-circle" color="green" /> {statusText}</span>;
				}

				updated.status = statusJSX;
				updated.searchValues = {
					...updated.searchValues,
					status: statusText,
				};
			}
			if (updated.subRows && updated.subRows.length > 0) {
				updated.subRows = this.patchStatusInTable(updated.subRows, systemStatusInfo);
			}
			return updated;
		});
	}

	getSystemStatus(macAddress) {
		let statusInfo = {};
		statusInfo.statusText = 'Not Connected';
		statusInfo.statusJSX = <span className="errorIcon"><CustomIcon icon="exclamation-circle" /> {statusInfo.statusText}</span>;

		let systemStatusInfo = this.state.systemStatusInfo;
		if (systemStatusInfo[macAddress]) {
			if (systemStatusInfo[macAddress].conn === 1) {
				statusInfo.statusText = 'Online';
				statusInfo.statusJSX = <span><CustomIcon icon="check-circle" color="green" /> {statusInfo.statusText}</span>;
			}
		}

		return statusInfo;
	}

	submitAndUpdateChanges(cb) {
		const {
			app,
		} = this.props;

		this.setState({
			systemsAboutToExpire: [],
		});

		app.saveDataAndCloseModal('system', cb);
	}

	// Handle moving a system to a different system_group
	async handleMoveSystem(systemUUID, sourceSystemGroupUUID, targetSystemGroupUUID) {
		const {
			app,
			accountUUID,
			systemGroupUUID,
			fd,
		} = this.props;

		const session = getSession();
		broncoSDK.setAuth(session.session, session.user);

		if (fd?.[`system.${systemUUID}`]?.assigned_to_point) {
			if (!confirm('This system is currently assigned to a point in a map. Moving it will remove that assignment. Are you sure you want to move this system?')) {
				return;
			}
		}

		try {
			const contents = await broncoSDK.moveSystem(
				systemUUID,
				sourceSystemGroupUUID,
				targetSystemGroupUUID
			);

			if (contents.status === 'success') {
				app.fetchAccount(accountUUID, systemGroupUUID);
			} else {
				console.error('System move failed:', contents.message);
				alert(`Failed to move system: ${contents.message || 'Unknown error'}`);
			}
		} catch (err) {
			console.error('System move request failed:', err);
			alert('Failed to move system. Please try again.');
		}
	}

	// Handle moving a system_group to a different parent (or to root)
	async handleMoveSystemGroup(systemGroupUUID, targetParentUUID, accountUUID) {
		const {
			app,
			systemGroupUUID: currentFilterUUID,
		} = this.props;

		const session = getSession();
		broncoSDK.setAuth(session.session, session.user);

		try {
			const contents = await broncoSDK.moveSystemGroup(
				systemGroupUUID,
				targetParentUUID,
				accountUUID
			);

			if (contents.status === 'success') {
				app.fetchAccount(accountUUID, currentFilterUUID);
			} else {
				console.error('System group move failed:', contents.message);
				alert(`Failed to move hierarchy: ${contents.message || 'Unknown error'}`);
			}
		} catch (err) {
			console.error('System group move request failed:', err);
			alert('Failed to move hierarchy. Please try again.');
		}
	}

	// Open confirmation modal for system group moves
	openMoveConfirmModal(systemGroupUUID, targetParentUUID) {
		const {
			fd,
			accountUUID,
		} = this.props;

		// Count descendants and systems in the subtree
		let descendantCount = 0;
		let systemCount = 0;

		const countDescendants = (sgUUID) => {
			const sgKey = `system_group.${sgUUID}`;
			if (fd[sgKey]) {
				for (const key of Object.keys(fd[sgKey])) {
					if (key.startsWith('system_group.')) {
						descendantCount++;
						const childUUID = key.split('.')[1];
						countDescendants(childUUID);
					} else if (key.startsWith('system.')) {
						systemCount++;
					}
				}
			}
		};

		countDescendants(systemGroupUUID);

		// Get the system group name
		const sgKey = `system_group.${systemGroupUUID}`;
		const sgName = fd[sgKey]?.display_name || 'Unknown';

		// Get the target name
		let targetName = 'account root level';
		if (targetParentUUID) {
			const targetKey = `system_group.${targetParentUUID}`;
			targetName = fd[targetKey]?.display_name || 'Unknown';
		}

		this.props.app.openModal({
			modalSize:  1,
			showModal:  true,
			modalProps: {
				title:          'Confirm Move',
				pendingChanges: false,
				jsx:            (
					<div style={{
						padding: '20px',
					}}>
						<p style={{
							marginBottom: '15px',
						}}>
							Moving <strong>"{sgName}"</strong> to <strong>"{targetName}"</strong> will include:
						</p>
						<ul style={{
							marginBottom: '20px',
							paddingLeft:  '20px',
						}}>
							<li>{descendantCount} sub-hierarch{descendantCount === 1 ? 'y' : 'ies'}</li>
							<li>{systemCount} system{systemCount === 1 ? '' : 's'}</li>
						</ul>
						<div style={{
							display:        'flex',
							justifyContent: 'flex-end',
							gap:            '10px',
						}}>
							<button
								className="button"
								onClick={() => {
									this.props.app.closeModal();
								}}
							>
								Cancel
							</button>
							<button
								className="button button-primary"
								onClick={() => {
									this.props.app.closeModal();
									this.handleMoveSystemGroup(systemGroupUUID, targetParentUUID, accountUUID);
								}}
							>
								Confirm Move
							</button>
						</div>
					</div>
				),
			},
		});
	}

	checkDataValidity() {
		const {
			app,
			settingMap,
		} = this.props;
		let keys = Object.keys(app.state.pendingChanges);
		let dataValid = true;
		keys.forEach((key, index) => {
			if (key.includes('display_name')) {
				if (app.state.pendingChanges[key] === '') {
					dataValid = false;
				}
			}
			if (key.includes(`online_expires`)) {
				if (app.state.pendingChanges[key] === '' || app.state.pendingChanges[key] === null) {
					dataValid = false;
				}
			}
		});

		return dataValid;
	}

	// DEV-12007 — one-shot import of the legacy 4.5 users of a migrated
	// property. The confirm is deliberate: the button disables forever after a
	// successful run (server enforces it too).
	// Step 1: fetch the read-only preview (who will be created/linked)
	// and open the confirmation modal. No writes happen until the admin confirms.
	// True while the modal is still showing the request we awaited. If the admin
	// closed it (or opened it for another group) mid-flight, the response must
	// not re-open or overwrite the modal.
	importModalStillOn(uuid, phase) {
		const m = this.state.importModal;
		return !!m && m.uuid === uuid && m.phase === phase;
	}

	async importLegacyUsers(uuid, displayName) {
		this.setState({ importModal: { phase: 'loading', uuid, displayName } });
		try {
			const session = getSession();
			const res = await axios.get(`${broncoURL}/migration/users/preview`, {
				params: { system_group_uuid: uuid },
				headers: { 'X-Auth-Session': session.session },
			});
			if (!this.importModalStillOn(uuid, 'loading')) return;
			const preview = res.data || {};
			// Every legacy user starts checked; the admin unchecks the ones to skip.
			const selected = new Set((preview.users || []).map((u) => u.email));
			this.setState({ importModal: { phase: 'confirm', uuid, displayName, preview, selected } });
		} catch (err) {
			if (!this.importModalStillOn(uuid, 'loading')) return;
			this.setState({ importModal: { phase: 'error', uuid, displayName, error: err?.response?.data?.message || 'Could not load the user list — check the bronco logs.' } });
		}
	}

	// Check/uncheck one legacy user in the confirm step.
	toggleImportUser(email) {
		const m = this.state.importModal;
		if (!m || m.phase !== 'confirm') return;
		const selected = new Set(m.selected);
		if (selected.has(email)) {
			selected.delete(email);
		} else {
			selected.add(email);
		}
		this.setState({ importModal: { ...m, selected } });
	}

	// Step 2: the admin confirmed — run the real import, then show results.
	// The import itself is one-shot on the server, so a closed modal only
	// suppresses the UI update; the account refresh still runs so the icon
	// reflects the new state.
	async confirmImportUsers() {
		const { uuid, displayName, preview, selected } = this.state.importModal;
		// Only the still-checked users go to bronco. Sending the list even when
		// everyone is checked keeps the request explicit.
		const previewedEmails = (preview?.users || []).map((u) => u.email);
		const emails = previewedEmails.filter((e) => selected.has(e));
		if (emails.length === 0) return;
		this.setState({ importModal: { phase: 'submitting', uuid, displayName, preview } });
		try {
			const session = getSession();
			const res = await axios.post(`${broncoURL}/migration/users`, {
				system_group_uuid: uuid,
				emails,
				// Lets bronco refuse (and release the one-shot) if the legacy
				// list changed since this preview was loaded.
				previewed_emails: previewedEmails,
			}, {
				headers: { 'X-Auth-Session': session.session },
			});
			this.props.app.fetchAccount(this.props.accountUUID);
			if (!this.importModalStillOn(uuid, 'submitting')) return;
			this.setState({ importModal: { phase: 'results', uuid, displayName, results: res.data || {} } });
		} catch (err) {
			if (!this.importModalStillOn(uuid, 'submitting')) return;
			this.setState({ importModal: { phase: 'error', uuid, displayName, error: err?.response?.data?.message || 'User import failed — check the bronco logs.' } });
		}
	}

	closeImportModal() {
		this.setState({ importModal: null });
	}

	// Maps a preview/outcome status to a human label + CSS badge class.
	importStatusMeta(status) {
		switch (status) {
			case 'new':
			case 'created':
				return { label: 'New User - will be created & emailed', done: 'User Created', badge: 'badgeNew' };
			case 'existing_link':
			case 'linked':
				return { label: 'Existing User - will be linked & emailed', done: 'Linked', badge: 'badgeLink' };
			case 'error':
				return { label: 'Lookup failed (import would error)', done: 'Error', badge: 'badgeError' };
			default:
				return { label: status, done: 'Error', badge: 'badgeError' };
		}
	}

	renderImportModal() {
		const m = this.state.importModal;
		if (!m) {
			return null;
		}

		let body;
		if (m.phase === 'loading' || m.phase === 'submitting') {
			body = (
				<>
					<h3 id="import-users-modal-title" className={style.importModalTitle}>{m.phase === 'loading' ? 'Loading users…' : 'Importing…'}</h3>
					<p className={style.importModalBody}>
						{m.phase === 'loading'
							? `Fetching the legacy users for "${m.displayName}".`
							: `Creating and linking users for "${m.displayName}". This can take a moment.`}
					</p>
				</>
			);
		} else if (m.phase === 'error') {
			body = (
				<>
					<h3 id="import-users-modal-title" className={style.importModalTitleError}>Something went wrong</h3>
					<p className={style.importModalBody}>{m.error}</p>
					<div className={style.importModalActions}>
						<button className={style.importModalCancel} onClick={() => this.closeImportModal()}>Close</button>
					</div>
				</>
			);
		} else if (m.phase === 'confirm') {
			const p = m.preview || {};
			const users = p.users || [];
			const total = users.length;
			const selected = m.selected || new Set();
			const chosen = users.filter((u) => selected.has(u.email));
			const count = chosen.length;
			const newCount = chosen.filter((u) => u.status === 'new').length;
			const linkCount = chosen.filter((u) => u.status === 'existing_link').length;
			body = (
				<>
					<h3 id="import-users-modal-title" className={style.importModalTitle}>Attention: Import &amp; Email Legacy Users ⚠️</h3>
					<div className={style.importWarning}>
						<strong>This will import the users for {p.property_name || m.displayName}</strong>
						{p.account_name ? <> in {p.account_name}</> : null} and send each created or linked
						user a welcome email. <strong>This can only be done once</strong> — users you uncheck
						here will not be imported and cannot be imported later.
					</div>
					<div className={style.importSummary}>
						<div>{newCount} new user(s)</div>
						<div>{linkCount} existing user(s)</div>
						{count < total ? <div>{total - count} unchecked — will be skipped</div> : null}
					</div>
					{total === 0 ? (
						<p className={style.importModalBody}>No users found for this property — nothing will be imported.</p>
					) : (
						<div className={style.importUserList}>
							{users.map((u) => {
								const meta = this.importStatusMeta(u.status);
								const checked = selected.has(u.email);
								return (
									<label className={`${style.importUserRow} ${checked ? '' : style.importUserRowSkipped}`} key={u.email}>
										<input
											type="checkbox"
											className={style.importUserCheck}
											checked={checked}
											onChange={() => this.toggleImportUser(u.email)}
											data-test-id={`import-user-check-${u.email}`}
										/>
										<span className={style.importUserEmail}>{u.email}</span>
										<span className={`${style.importBadge} ${style[meta.badge]}`}>{meta.label}</span>
										{u.error ? <span className={style.importUserReason}>{u.error}</span> : null}
									</label>
								);
							})}
						</div>
					)}
					<div className={style.importModalActions}>
						<button className={style.importModalCancel} onClick={() => this.closeImportModal()}>Cancel</button>
						<button
							className={style.importModalConfirm}
							disabled={count === 0}
							onClick={() => this.confirmImportUsers()}
						>
							{total === 0
								? 'Nothing to import'
								: count === 0
									? 'No users selected'
									: `Import ${count} of ${total} user${total === 1 ? '' : 's'}`}
						</button>
					</div>
				</>
			);
		} else if (m.phase === 'results') {
			const r = m.results || {};
			const users = r.users || [];
			body = (
				<>
					<h3 id="import-users-modal-title" className={style.importModalTitle}>Import complete</h3>
					<div className={style.importSummary}>
						<div>{r.created || 0} user(s) created</div>
						<div>{r.linked || 0} user(s) linked</div>
						<div>{r.errors || 0} user(s) errored</div>
						<div>{r.emails_queued || 0} welcome email(s) sent</div>
					</div>
					{r.message ? <p className={style.importWarning} data-test-id="import-users-result-message">{r.message}</p> : null}
					{users.length > 0 && (
						<div className={style.importUserList}>
							{users.map((u) => {
								const meta = this.importStatusMeta(u.outcome);
								return (
									<div className={style.importUserRow} key={u.email}>
										<span className={style.importUserEmail}>{u.email}</span>
										<span className={`${style.importBadge} ${style[meta.badge]}`}>
											{meta.done}{u.email_queued ? ' • emailed' : ''}{u.role_kept ? ` • kept ${u.role_kept}` : ''}
										</span>
										{u.error ? <span className={style.importUserReason}>{u.error}</span> : null}
									</div>
								);
							})}
						</div>
					)}
					<div className={style.importModalActions}>
						<button className={style.importModalConfirm} onClick={() => this.closeImportModal()}>Done</button>
					</div>
				</>
			);
		}

		return (
			<div className={style.importModalOverlay} onClick={() => { if (m.phase !== 'submitting') this.closeImportModal(); }}>
				<div
					className={style.importModal}
					onClick={(e) => e.stopPropagation()}
					role="dialog"
					aria-modal="true"
					aria-labelledby="import-users-modal-title"
				>
					{body}
				</div>
			</div>
		);
	}

	addButtonsToSystemGroup(display_name, uuid) {
		const {
			fd,
			app,
			accountUUID,
			systemGroupUUID,
			setSystemGroupUUID,
		} = this.props;

		let displayName = display_name;
		let onlineExpires = null;
		let projectNumber = fd[`system_group.${uuid}`]['project_number'] || '';
		let projectNumberStyle = projectNumber ? 'projectNumber' : '';

		for (let [ m2Key, m2Data, ] of Object.entries(fd[`system_group.${uuid}`])) {
			if (m2Key === 'online_expires') {
				let dateTimezone = m2Data || '';
				let yearMonthDate = dateTimezone.split('T')[0];
				let ymd = yearMonthDate.split('-');
				let y = ymd[0];
				let m = ymd[1];
				let d = ymd[2];
				// Skip the renewal-warning block entirely if the value isn't
				// a parseable YYYY-MM-DD — otherwise NaN cascades through the
				// math and the message renders "undefined.undefined.".
				if (!y || !m || !d || isNaN(Number(y)) || isNaN(Number(m)) || isNaN(Number(d))) {
					continue;
				}
				let todayDate = new Date();
				// Construct from local components so the renewal-warning math
				// reflects the calendar day Atlas/the DB stored, not midnight
				// UTC interpreted in the browser's local zone.
				let expirationDate = new Date(Number(y), Number(m) - 1, Number(d));
				let paymentWarning = (Math.round(todayDate - expirationDate) / (1000 * 60 * 60 * 24 * -1)) < 90;

				if (paymentWarning) {
					let expirationArray = this.state.systemsAboutToExpire;
					expirationArray.push(expirationDate);
					this.setState({
						systemsAboutToExpire: expirationArray,
					});
				}

				let diffTime = Math.abs(todayDate - expirationDate);
				let diffDays = 0;
				let expirationMessage = ` Subscription expired on ${m}.${d}.${y}. Updates made in the user portal will not publish to display. Please contact TouchSource (renewals@touchsource.com) to renew.`;

				if (todayDate > expirationDate) {
					diffDays = Math.ceil(-diffTime / (1000 * 60 * 60 * 24));
				} else {
					diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
					expirationMessage = ` Subscription renewal date: ${m}.${d}.${y}. Please renew within ${diffDays} days.`;
				}

				onlineExpires = (
					<div className={style.expirationContainer}>
						{paymentWarning ? <p className={`${diffDays <= 90 && diffDays >= 60 ? style.yellowwarning : ''} 
							${diffDays < 60 && diffDays >= 30 ? style.orangewarning : ''}
							${diffDays < 30 ? style.redwarning : ''}`}>{expirationMessage}</p> : null}
					</div>
				);

				if (this.getEarliestExpiration() === diffDays) {
					app.setState({
						expirationDate: expirationMessage,
					});
				}
			}
		}

		if (hasGlobalScope()) {
			let editIcon = (
				<div
					title="Edit Hierarchy"
					data-test-id={`editHierarchy--${display_name}`}
					onClick={(e) => {
						e.preventDefault();
						this.openEditModal(uuid);
					}}
				>
					<CustomIcon icon='edit' color='#00BAFF' />
				</div>
			);

			let subGroup = (
				<div
					title="Add Sub Hierarchy"
					data-test-id={`addSubHierarchy--${display_name}`}
					onClick={(e) => {
						app.openModal({
							modalSize:  2,
							showModal:  true,
							modalProps: {
								title:          'New Hierarchy',
								pendingChanges: false,
								jsx:            (
									<SystemsModal
										app={app}
										accountUUID={accountUUID}
										hierarchySystemGroupUUID={systemGroupUUID}
										fd={this.props.fd}
										isEdit={false}
										save={this.submitAndUpdateChanges.bind(this)}
										checkDataValidity={this.checkDataValidity.bind(this)}
										parentUUID={uuid}
									/>
								),
							},
						});
					}}
				>
					<CustomIcon icon='folder-plus' color='#00BAFF' />
				</div>
			);

			let subSystem = (
				<div
					title="Add system"
					data-test-id={`addSystem--${display_name}`}
					onClick={(e) => {
						this.props.app.openModal({
							modalSize:  2,
							showModal:  true,
							modalProps: {
								title:          'New System',
								pendingChanges: false,
								jsx:            (
									<SystemsModal
										app={this.props.app}
										accountUUID={this.props.accountUUID}
										fd={this.props.fd}
										isEdit={false}
										save={this.submitAndUpdateChanges.bind(this)}
										checkDataValidity={this.checkDataValidity.bind(this)}
										parentUUID={uuid}
										onlyAddSystems={true}
									/>
								),
							},
						});
					}}
				>
					<CustomIcon icon='plus-circle' color='#00BAFF' />
				</div>
			);

			let filterCheckbox = (
				<div
					title="Activate System Group Filter"
					onClick={() => {
						setSystemGroupUUID(uuid);
					}}
				>
					<CustomIcon icon='filter' color='#BABABA' />
				</div>
			);

			if (systemGroupUUID === uuid) {
				filterCheckbox = (
					<div
						title="Deactivate System Group Filter"
						onClick={() => {
							setSystemGroupUUID('');
						}}
					>
						<CustomIcon icon='filter' color='#00BAFF' />
					</div>
				);
			}

			// DEV-12007 — Import Users icon: gated on tools_access (the same
			// permission the /migration/users handlers check — never a role
			// name), only on migration-created groups (migrated_at from the FD
			// payload) with a numeric legacy property number (mirrors the
			// server's canImportUsers so the icon never offers a guaranteed
			// 409), and permanently grayed once users_migrated_at is set.
			let importUsersIcon = null;
			const legacyPropertyNumber = String(fd[`system_group.${uuid}`]['project_number'] || '');
			if (hasPermission('tools_access') && fd[`system_group.${uuid}`]['migrated_at'] && /^[0-9]+$/.test(legacyPropertyNumber)) {
				const usersMigratedAt = fd[`system_group.${uuid}`]['users_migrated_at'];
				if (usersMigratedAt) {
					importUsersIcon = (
						<div
							className={style.iconDisabled}
							title={`User import ran ${String(usersMigratedAt).split('T')[0]}`}
							data-test-id={`importUsersDone--${display_name}`}
						>
							<CustomIcon icon='user-plus' color='#BABABA' />
						</div>
					);
				} else {
					importUsersIcon = (
						<div
							title="Import legacy 4.5 users"
							data-test-id={`importUsers--${display_name}`}
							onClick={(e) => {
								e.preventDefault();
								this.importLegacyUsers(uuid, display_name);
							}}
						>
							<CustomIcon icon='user-plus' color='#00BAFF' />
						</div>
					);
				}
			}

			displayName = (
				<div
					className={style.oneLine}
				>
					<div className={style[projectNumberStyle]} data-test-id="projectNumber">{projectNumber}</div>
					<div className={style.displayName} data-test-id="systemDisplayName">{display_name}</div>
					<div className={style.icons}>{editIcon} {subGroup} {subSystem} {filterCheckbox} {importUsersIcon}</div>
					<div className={style.onlineExpires}>{onlineExpires}</div>
				</div>
			);
		} else {
			displayName = (
				<div
					className={style.oneLine}
				>
					<div className={style[projectNumberStyle]} data-test-id="projectNumber">{projectNumber}</div>
					<div className={style.displayName} data-test-id="systemDisplayName">{display_name}</div>
					<div className={style.onlineExpires}>{onlineExpires}</div>
				</div>
			);
		}

		return displayName;
	}

	findInitialSystemGroupsForTable() {
		const {
			fd,
			accountUUID,
			systemGroupUUID,
			settingMap,
		} = this.props;

		let systemGroupObj = {};
		let updatedSystemGroupObj = {};

		const filteredFD = getFilteredHierarchy(fd, 'account.' + accountUUID, systemGroupUUID);

		for (const [ m1Key, m1Data, ] of Object.entries(filteredFD)) {
			if (m1Key === `account.${accountUUID}`) {
				for (let m2Key of Object.keys(m1Data)) {
					if (m2Key.startsWith('system_group.')) {
						let m2Data = fd[m2Key];
						let m2UUID = m2Key.split('.')[1];
						let m2Name = m2Data['display_name'] || '';
						let projectNumber = m2Data['project_number'] || '';

						let hideGroup = false;

						// Property-scoped users don't see groups with no visible systems.
						if (isPropertyScoped()) {
							hideGroup = true;
							for (let [ systemKey, ] of Object.entries(m2Data)) {
								if (systemKey.startsWith('system.') || systemKey.startsWith('system_group.')) {
									if (systemKey.startsWith('system.') && fd[systemKey]) {
										hideGroup = false;
										break;
									}
									if (systemKey.startsWith('system_group.') && fd[systemKey]) {
										for (let [ subGroupSystemkey, ] of Object.entries(fd[systemKey])) {
											if (subGroupSystemkey.startsWith('system.')) {
												hideGroup = false;
												break;
											}
										}
									}
								}
							}
						}

						if (!hideGroup) {
							systemGroupObj[m2Key] = {
								id:           m2UUID,
								name:         this.addButtonsToSystemGroup(m2Name, m2UUID),
								m1:           'system_group',
								projectNumber,
								status:       '',
								subRows:      [],
								components:   [],
								path:         '',
								searchValues: {
									name:            m2Name,
									projectNumber,
									resolution:      '',
									status:          '',
									macAddress:      '',
									orientation:     '',
									directoryNumber: '',
								},
							};
						}
						updatedSystemGroupObj = this.findChildrenForTable(m2UUID, systemGroupObj);
					}
				}
			}
		}

		return updatedSystemGroupObj;
	}

	findSystemGroupsForTable(m1ParentKey, systemGroupObj) {
		const {
			fd,
			systemGroupUUID,
		} = this.props;

		const filteredFD = getFilteredHierarchy(fd, m1ParentKey, systemGroupUUID);

		let updatedSystemGroupObj = systemGroupObj;

		for (const [ m1Key, m1Data, ] of Object.entries(filteredFD)) {
			if (m1ParentKey === m1Key) {
				for (let m2Key of Object.keys(m1Data)) {
					if (m2Key.startsWith('system_group.')) {
						let m2Data = fd[m2Key];
						let m2UUID = m2Key.split('.')[1];
						let m2Name = m2Data['display_name'];
						let hideGroup = false;

						// Property-scoped users don't see groups with no visible systems.
						if (isPropertyScoped()) {
							hideGroup = true;
							for (const [ systemKey, ] of Object.entries(m2Data)) {
								if (systemKey.startsWith('system.') || systemKey.startsWith('system_group.')) {
									if (fd[systemKey]) {
										hideGroup = false;
										break;
									}
								}
							}
						}

						const alreadyASubRow = systemGroupObj.subRows.findIndex((element) => {
							if (element.id === m2UUID) {
								return true;
							}
						}) !== -1;

						if (alreadyASubRow) {
							continue;
						}

						let projectNumber = m2Data['project_number'] || '';
						if (!hideGroup) {
							systemGroupObj.subRows.push({
								id:           m2UUID,
								name:         this.addButtonsToSystemGroup(m2Name, m2UUID),
								m1:           'system_group',
								status:       '',
								subRows:      [],
								components:   [],
								path:         '',
								searchValues: {
									name:            m2Name,
									projectNumber,
									resolution:      '',
									status:          '',
									macAddress:      '',
									orientation:     '',
									directoryNumber: '',
								},
							});
						}
						updatedSystemGroupObj = this.findChildrenForTable(m2UUID, systemGroupObj);
					}
				}
			}
		}

		return updatedSystemGroupObj;
	}

	findChildrenForTable(id, systemGroupObj) {
		const {
			fd,
			accountUUID,
			systemGroupUUID,
		} = this.props;

		const filteredFD = getFilteredHierarchy(fd, 'account.' + accountUUID, systemGroupUUID);

		let systemM1Keys = [];
		for (let [ m1Key, m1Data, ] of Object.entries(filteredFD)) {
			if (m1Key === `system_group.${id}`) {
				for (let m2Key of Object.keys(m1Data)) {
					if (m2Key.startsWith('system.')) {
						systemM1Keys.push(m2Key);
					}
				}
			}
		}

		let systemUUIDToTLCUUIDMap = {};
		for (let m1Key of systemM1Keys) {
			let m1UUID = m1Key.split('.')[1];
			for (let m2Key of Object.keys(fd[m1Key])) {
				if (m2Key.startsWith('component.')) {
					let m2Data = fd[m2Key];
					let m2UUID = m2Key.split('.')[1];

					systemUUIDToTLCUUIDMap[m1UUID] = m2UUID;
				}
			}
		}

		for (const [ m1Key, m1Data, ] of Object.entries(filteredFD)) {
			if (m1Key === `system_group.${id}`) {
				let m1UUID = m1Key.split('.')[1];

				for (let m2Key of Object.keys(m1Data)) {
					if (m2Key.startsWith('system.')) {
						let m2Data = fd[m2Key];
						let m2UUID = m2Key.split('.')[1];
						let m2Name = m2Data['display_name'];

						// Systems flagged hidden are for internal staff only. The old test also
						// exempted content_manager, which let content managers see hidden systems;
						// that was not intended, so slideshow-scoped users are now filtered too.
						if (!hasGlobalScope() && m2Data['hidden']) {
							continue;
						}

						let orientationValue = m2Data['orientation'] || '';
						switch (orientationValue) {
						case 'landscape':
							orientationValue = 'Landscape';
							break;
						case 'landscapeflipped':
							orientationValue = 'Landscape (flipped)';
							break;
						case 'portraitleft':
							orientationValue = 'Portrait (left)';
							break;
						case 'portraitright':
							orientationValue = 'Portrait (right)';
							break;
						default:
							// orientationValue = '';
						}

						let components = this.findComponentsForTable(m2UUID);
						let componentOrientation = components.orientation;
						if (componentOrientation !== 'vertical' && componentOrientation !== 'horizontal') {
							componentOrientation = 'no-preview';
						}
						let componentResolution = components.resolutionForSearch;
						if (!componentResolution) {
							componentResolution = '';
						}

						let resolutionForSearch = '';
						let resolution = '';
						if (m2Data['resolution']) {
							resolutionForSearch = m2Data['resolution'];

							resolution = (
								<div className={style.resolution}>
									{resolutionForSearch.includes(3840) ? <>{resolutionForSearch} <CustomIcon icon="4k" /></> : `${resolutionForSearch}`}
								</div>
							);
						}

						let env = vultureENV;
						let tlcUUID = systemUUIDToTLCUUIDMap[m2UUID];

						let d = new Date();
						let screenshotSrc = s3PublicURL('ts-screen-shots', `${env}/${tlcUUID}.jpg#${d.getFullYear()}-${d.getMonth()}-${d.getDay()}`);

						let orientation = (
							<div
								data-test-id="systemPreviewWindow"
								className={style.orientationIcon}
								onClick={(e) => {
									e.preventDefault();

									dlImagePreviewPush();

									e.stopPropagation();
									this.openPreviewModal(screenshotSrc, m2Name, env, m2Key);
								}}
							>
								<SystemErrorIcon
									src={screenshotSrc}
								/>
							</div>
						);
						let isSystemEnabled = this.checkForDisabledSystems(m2UUID);
						let animDropdown = <></>;

						if (this.state.accountDropdownEnabled && isSystemEnabled) {
							animDropdown = <>
								<span>Active screen: </span>
								<Select
									menuPlacement="auto"
									menuPosition='fixed'
									options={this.state.accountDropdownJson.options}
									value={this.getTLCAnimValues(m2UUID) || {
										value: '',
										label: '',
									}}
									onChange={(e) => {
										this.handleChange(m2UUID, e);
									}}
								/>
							</>;
						}

						let hasMobile = false;

						if (fd?.['component.' + tlcUUID]) {
							hasMobile = Object.keys(fd['component.' + tlcUUID]).findIndex((m2Key) => {
								if (fd[m2Key]?.description === 'mobile') {
									return true;
								}
							}) !== -1;
						}

						const nameWithLinks = (
							<div
								className={style.systemName}
							>
								<div
									alt='Open Preview'
									className={style.condorPreviewIcon}
									onClick={(e) => {
										e.stopPropagation();

										if (!tlcUUID) {
											return;
										}

										this.openIFramePreviewModal(m2Name, `system.${m2UUID}`);
									}}
								>
									<CustomIcon icon='eye' color={tlcUUID ? '#4BA046' : '#4B4B4B'} />
								</div>
								<div
									alt='Open Mobile App'
									className={style.condorMobileIcon}
									onClick={(e) => {
										e.stopPropagation();

										if (!tlcUUID || !hasMobile) {
											return;
										}

										this.openIframeMobileModal(m2Name, tlcUUID);
									}}
								>
									<CustomIcon icon='mobile-alt' color={(tlcUUID && hasMobile) ? '#4BA046' : '#aeaeae'} />
								</div>
								{m2Name}
							</div>
						);

						let orientationAndName = (
							<>
								<div className={style.systemTitle}>
									{orientation} {nameWithLinks}
								</div>
								<div className={style.animDropdown}>
									{animDropdown}
								</div>
							</>
						);

						let macAddress = m2Data['mac_address'] || '';
						let directoryNumber = m2Data.directory_number;
						let statusInfo = this.getSystemStatus(macAddress);

						// macAddress stays the raw MAC (status lookups, the detail
						// modal); macAddressCell is what the MAC column renders
						const isPWA = !!m2Data['pwa'];
						let macAddressCell = macAddress;
						if (isPWA) {
							statusInfo = {
								statusText: 'PWA',
								statusJSX:  <span>PWA</span>,
							};
							const pwaURL = getPWAURL(systemUUIDToTLCUUIDMap[m2UUID]);
							macAddressCell = pwaURL ? <PWACopyURLButton url={pwaURL} /> : <span>{PWA_NO_COMPONENT_TEXT}</span>;
						}

						if (systemGroupObj[m1Key]) {
							systemGroupObj[m1Key].subRows.push({
								id:           m2UUID,
								name:         orientationAndName,
								m1:           'system', // m2,
								macAddress,
								macAddressCell,
								isPWA,
								resolution,
								directoryNumber,
								orientationValue,
								status:       statusInfo.statusJSX,
								subRows:      [],
								components,
								searchValues: {
									name:            m2Name,
									projectNumber:   '',
									resolution:      resolutionForSearch,
									status:          statusInfo.statusText,
									macAddress:      (m2Data.mac_address) ? m2Data.mac_address.replaceAll(':', '') : '',
									orientation:     orientationValue,
									directoryNumber: (m2Data.directory_number) ? m2Data.directory_number.toString() : '',
								},
							});
							systemGroupObj[m1Key].subRows.sort(this.sortByM1AndName);
						} else if (systemGroupObj.subRows) {
							systemGroupObj.subRows.forEach((subRow, i) => {
								if (subRow.id === m1UUID) {
									const alreadyASubRow = systemGroupObj.subRows[i].subRows.findIndex((element) => {
										if (element.id === m2UUID) {
											return true;
										}
									}) !== -1;

									if (alreadyASubRow) {
										return;
									}

									let directoryNumber = m2Data.directory_number;
									systemGroupObj.subRows[i].subRows.push({
										id:           m2UUID,
										name:         orientationAndName,
										m1:           'system', // m2,
										macAddress,
										macAddressCell,
										isPWA,
										resolution,
										status:       statusInfo.statusJSX,
										orientationValue,
										directoryNumber,
										subRows:      [],
										components,
										searchValues: {
											name:            m2Name,
											projectNumber:   '',
											resolution:      resolutionForSearch,
											status:          statusInfo.statusText,
											macAddress:      (m2Data.mac_address) ? m2Data.mac_address.replaceAll(':', '') : '',
											orientation:     orientationValue,
											directoryNumber: (m2Data.directory_number) ? m2Data.directory_number.toString() : '',
										},
									});
									systemGroupObj.subRows[i].subRows.sort(this.sortByM1AndName);
								}
							});
						}
					}
					if (m2Key.startsWith('system_group.')) {
						if (systemGroupObj[m1Key]) {
							systemGroupObj[m1Key] = this.findSystemGroupsForTable(m1Key, systemGroupObj[m1Key]);
						}
					} else if (systemGroupObj.subRows) {
						systemGroupObj.subRows = systemGroupObj.subRows.map((subRow) => {
							if (subRow.id === m1UUID) {
								subRow = this.findSystemGroupsForTable(m1Key, subRow);
							}
							return subRow;
						});
					}
				}
			}
		}

		return systemGroupObj;
	}

	findComponentsForTable(id) {
		const {
			fd,
		} = this.props;

		let updatedComponentObj = {};
		for (let [ m1Key, m1Data, ] of Object.entries(fd)) {
			if (m1Key === `system.${id}`) {
				for (let [ m2Key, ] of Object.entries(m1Data)) {
					if (m2Key.startsWith('component.')) {
						let m2UUID = m2Key.split('.')[1];

						updatedComponentObj = this.findComponentSettings(m2UUID, updatedComponentObj);

						updatedComponentObj.id = m2UUID;
						updatedComponentObj.name = m2UUID;
						updatedComponentObj.m1 = 'component';
					}
				}
			}
		}

		return updatedComponentObj;
	}

	findComponentSettings(id, obj) {
		const {
			fd,
		} = this.props;

		const {
			condorThemeID,
			orientationID,
			resolutionID,
		} = this.state;

		obj.condor_theme = getFDValue(fd, `component.${id}.setting.${condorThemeID}.string`);
		obj.orientation = getFDValue(fd, `component.${id}.setting.${orientationID}.string`);
		obj.resolutionForSearch = getFDValue(fd, `component.${id}.setting.${resolutionID}.string`);
		if (obj.resolutionForSearch) {
			obj.resolution = (
				<div className={style.resolution}>
					{obj.resolutionForSearch.includes(3840) ? <>{obj.resolutionForSearch} <CustomIcon icon="4k" /></> : `${obj.resolutionForSearch}`}
				</div>
			);
		}

		return obj;
	}

	handleChange(system, event) {
		const {
			app,
			settingMap,
		} = this.props;

		let setting = settingMap['animation_start_phase'];
		let componentKey = '';

		Object.keys(app.state.accountFD)
			.forEach((m1Key) => {
				if (m1Key.startsWith('system.') && m1Key.split('.')[1] === system) {

					Object.keys(app.state.accountFD[m1Key]).forEach((m2Key) => {

						if (m2Key.startsWith('component.')) {
							componentKey = m2Key.split('.')[1];
							let entityKey = `component.${componentKey}.setting.${setting.uuid}.${setting.type}`;
							app.setValue('tlc_setting', entityKey, event.value, () => {
								app.saveData();
							});
						}
					});
				}
			});
	}

	checkForDisabledSystems(systemUUID) {
		const {
			app,
		} = this.props;

		const {
			accountFD,
		} = app.state;

		if (accountFD[`system.${systemUUID}`] !== undefined) {
			let keysStr = Object.keys(accountFD[`system.${systemUUID}`]).join('');
			return Boolean(~keysStr.indexOf('component.'));
		}

		return false;

	}

	getTLCAnimValues(systemid) {
		const {
			app,
			fd,
		} = this.props;

		let newValue = {
			label: '',
			value: '',
		};

		let componentKey = '';
		Object.keys(app.state.accountFD)
			.forEach((m1Key) => {
				if (m1Key.startsWith('system.') && m1Key.split('.')[1] === systemid) {

					Object.keys(app.state.accountFD[m1Key]).forEach((m2Key) => {

						if (m2Key.startsWith('component.')) {

							componentKey = m2Key.split('.')[1];
							let dropdownValue = '';
							dropdownValue = getFDValue(fd, `component.${componentKey}.setting.${app.state.settingMap['animation_start_phase'].uuid}.string`);

							(this.state.accountDropdownJson?.options || []).forEach((option) => {
								if (option.value === dropdownValue) {
									newValue = {
										label: option.label,
										value: option.value,
									};
								}
							});
						}
					});
				}
			});
		return newValue;
	}

	toggleTree(target) {
		if (target) {
			let sibling = target.nextSibling;
			if (sibling) {
				if (sibling.classList.contains(style.hide)) {
					sibling.classList.remove(style.hide);
					sibling.classList.add(style.show);
				} else {
					sibling.classList.remove(style.show);
					sibling.classList.add(style.hide);
				}

				this.toggleTree(sibling);
			}
		}
	}

	sortByName(a, b) {
		let aName = a.searchValues.name || '';
		let bName = b.searchValues.name || '';

		if (aName.toLowerCase() > bName.toLowerCase()) {
			return 1;
		}

		return -1;
	}

	sortByM1AndName(a, b) {
		const typeOrder = { 'system_group': 0, 'system': 1 };
		const ta = typeOrder[a.m1] ?? 99;
		const tb = typeOrder[b.m1] ?? 99;
		if (ta !== tb) return ta - tb;
		const aName = (a.searchValues?.name || a.name || '').toString().toLowerCase();
		const bName = (b.searchValues?.name || b.name || '').toString().toLowerCase();
		if (aName > bName) return 1;
		if (aName < bName) return -1;
		return 0;
	}

	openPreviewModal(src, entityName, env, systemUUID) {
		const {
			fd,
		} = this.props;

		let width = 1920;
		let height = 1080;

		Object.entries(fd[systemUUID]).forEach(([ key, val, ]) => {
			if (key === 'resolution') {
				[ width, height, ] = val.split('x').map((dimension) => {
					return Number(dimension);
				});
			}
		});

		// Calculates the largest possible size the iframe can have to fit into 80% of the screen's height or width
		const windowHeight = window.innerHeight;
		const windowWidth = window.innerWidth;
		const maxIframeWidth = windowWidth * 0.8;
		const maxIframeHeight = windowHeight * 0.8;

		const widthScaleFactor = maxIframeWidth / width;
		const heightScaleFactor = maxIframeHeight / height;
		const scaleFactor = Math.min(widthScaleFactor, heightScaleFactor);

		const scaledWidth = width * scaleFactor;
		const scaledHeight = height * scaleFactor;

		const containerStyle = {
			width:    scaledWidth + 'px',
			height:   scaledHeight + 'px',
			overflow: 'hidden',
		};

		const imageStyle = {
			width:     '100%',
			height:    '100%',
			objectFit: 'contain',
		};

		this.props.app.openModal({
			showModal:  true,
			modalSize:  5,
			modalProps: {
				title: entityName,
				jsx:   (
					<div className={style.previewFullContainer} style={containerStyle}>
						<Img
							className={style.previewFull}
							style={imageStyle}
							src={src}
						/>
						{/* Buttons to eventually be hooked up */}
						<div className={style.previewButtons}>
							<button
								className="button"
								onClick={() => {
									this.openIFramePreviewModal(entityName, systemUUID);
								}}>
								<CustomIcon icon="link" />Preview
							</button>
							<button className="button button-disabled"><CustomIcon icon="link" />Connect</button>
							<button className="button button-disabled"><CustomIcon icon="info" />Identify</button>
						</div>
					</div>
				),
			},
		});
	}

	openIFramePreviewModal(entityName, systemUUID) {
		const {
			fd,
		} = this.props;

		let condorComponent = '';
		let condorURL = '';
		let width = 1920;
		let height = 1080;

		Object.entries(fd[systemUUID]).forEach(([ key, val, ]) => {
			if (key.startsWith('component.')) {
				condorComponent = key.slice(10);
			} else if (key === 'resolution') {
				[ width, height, ] = val.split('x').map((dimension) => {
					return Number(dimension);
				});
			}
		});

		// Calculates the largest possible size the iframe can have to fit into 80% of the screen's height or width
		const windowHeight = window.innerHeight;
		const windowWidth = window.innerWidth;
		const maxIframeWidth = windowWidth * 0.8;
		const maxIframeHeight = windowHeight * 0.8;

		const widthScaleFactor = maxIframeWidth / width;
		const heightScaleFactor = maxIframeHeight / height;
		const scaleFactor = Math.min(widthScaleFactor, heightScaleFactor);

		const scaledWidth = width * scaleFactor;
		const scaledHeight = height * scaleFactor;

		condorURL = window._getEnv('PREVIEW_URL');

		const containerStyle = {
			width:    scaledWidth + 'px',
			height:   scaledHeight + 'px',
			overflow: 'hidden',
		};

		const iframeStyle = {
			transform:       `scale(${scaleFactor})`,
			transformOrigin: '0 0',
			border:          0,
		};

		this.props.app.openModal({
			showModal:  true,
			modalSize:  5,
			modalProps: {
				title: `Preview ${entityName}`,
				jsx:   (
					<div className={style.previewIFrameFullContainer} style={containerStyle}>
						<iframe
							className={style.previewIFrame}
							width={width}
							height={height}
							style={iframeStyle}
							src={`${condorURL}/?condor_component=${condorComponent}`}
							title="Preview">
						</iframe>
					</div>
				),
			},
		});
	}

	openIframeMobileModal(entityName, tlcUUID) {
		let width = 540;
		let height = 960;

		// Calculates the largest possible size the iframe can have to fit into 80% of the screen's height or width
		const windowHeight = window.innerHeight;
		const windowWidth = window.innerWidth;
		const maxIframeWidth = windowWidth * 0.8;
		const maxIframeHeight = windowHeight * 0.8;

		const widthScaleFactor = maxIframeWidth / width;
		const heightScaleFactor = maxIframeHeight / height;
		const scaleFactor = Math.min(widthScaleFactor, heightScaleFactor);

		const scaledWidth = width * scaleFactor;
		const scaledHeight = height * scaleFactor;

		let mobileURL = '';
		mobileURL = window._getEnv('CONDOR_MOBILE_URL');

		const containerStyle = {
			width:    scaledWidth + 'px',
			height:   scaledHeight + 'px',
			overflow: 'hidden',
		};

		const iframeStyle = {
			transform:       `scale(${scaleFactor})`,
			transformOrigin: '0 0',
			border:          0,
		};

		this.props.app.openModal({
			showModal:  true,
			modalSize:  6,
			modalProps: {
				title: `Mobile Preview ${entityName}`,
				jsx:   (
					<div className={style.previewIFrameFullContainer} style={containerStyle}>
						<iframe
							className={style.previewIFrame}
							width={width}
							height={height}
							style={iframeStyle}
							src={`${mobileURL}/?id=${tlcUUID}`}
							title="Mobile Preview">
						</iframe>
					</div>
				),
			},
		});
	}

	getTableData(uuid, parentTableData = {}) {
		for (let tableData of parentTableData) {
			if (tableData.id === uuid) {
				return tableData;
			}

			let childTableData = this.getTableData(uuid, tableData.subRows);
			if (childTableData.id === uuid) {
				return childTableData;
			}
		}

		return {};
	}

	openEditModal(uuid) {
		const {
			fd,
		} = this.props;

		const {
			systemsTableData,
		} = this.state;

		let systemGroup = this.getTableData(uuid, systemsTableData);

		let parentEntity = {};
		for (let [ m1Key, m1Data, ] of Object.entries(fd)) {
			for (let m2Key of Object.keys(m1Data)) {
				if (m2Key === `system_group.${uuid}`) {
					let s = m1Key.split('.');

					parentEntity = {
						m1:   s[0],
						m1ID: s[1],
					};
				}
			}
		}

		this.props.app.openModal({
			modalSize:  2,
			showModal:  true,
			modalProps: {
				title:          'Edit Hierarchy',
				pendingChanges: false,
				jsx:            (
					<SystemsModal
						app={this.props.app}
						accountUUID={this.props.accountUUID}
						fd={this.props.fd}
						isEdit={true}
						save={this.submitAndUpdateChanges.bind(this)}
						checkDataValidity={this.checkDataValidity.bind(this)}
						systemGroup={systemGroup}
						parentEntity={parentEntity}
					/>
				),
			},
		});
	}

	openSystemsDetailModal(uuid, macAddress) {
		const {
			app,
			accountUUID,
			systemGroupUUID,
			fd,
		} = this.props;

		app.openModal({
			modalSize:  2,
			showModal:  true,
			modalProps: {
				title:          'Edit System',
				pendingChanges: false,
				jsx:            (
					<SystemsDetailModal
						app={app}
						accountUUID={accountUUID}
						hierarchySystemGroupUUID={systemGroupUUID}
						fd={fd}
						save={this.submitAndUpdateChanges.bind(this)}
						checkDataValidity={this.checkDataValidity.bind(this)}
						systemStatusInfo={this.state.systemStatusInfo}
						uuid={uuid}
						macAddress={macAddress}
					/>
				),
			},
		});
	}

	updateSearch(columnName, value) {
		this.setState({
			macAddressFilter:      '',
			statusFilter:          '',
			nameFilter:            '',
			directoryNumberFilter: '',
			projectNumberFilter:   '',
			[columnName]:          value.replaceAll(':', ''),
		});

		if (columnName === 'directoryNumberFilter') {
			this.expandAllRows();
		} else if (value.length >= 3) {
			this.expandAllRows();
		}
	}

	getFilteredData() {
		const {
			systemsTableData,
			nameFilter,
		} = this.state;

		let shallowCopiedTableData = systemsTableData.sort(this.sortByName);
		let sortedTableData = this.deepCopyFunction(shallowCopiedTableData);
		sortedTableData.forEach((systemGroup) => {
			this.filterEntity(systemGroup);
		});
		this.addParentToRows(sortedTableData);
		this.addMatchingToRows(sortedTableData);
		if (nameFilter.length > 0) {
			this.makeSiblingsAvailable(sortedTableData);
		}
		const filteredData = this.removeUnmatchedRows(sortedTableData);
		return filteredData;
	}

	deepCopyFunction(inObject) {
		let key, outObject, value;

		if (typeof inObject !== 'object' || inObject === null) {
			return inObject;
		}

		outObject = Array.isArray(inObject) ? [] : {};

		for (key in inObject) {
			value = inObject[key];
			outObject[key] = this.deepCopyFunction(value);
		}

		return outObject;
	}

	removeUnmatchedRows(rows) {
		const {
			systemGroupUUID,
		} = this.props;

		const items = rows
			.filter((item) => {
				return item.matchesSearch;
			})
			.filter((item) => {
				if (!systemGroupUUID) {
					return true;
				}

				if (item.m1 !== 'system_group') {
					return true;
				}

				if (item.id === systemGroupUUID) {
					return true;
				}

				const allSubRows = combineSubrows(item);

				if (allSubRows.findIndex((subRow) => {
					return subRow.id === systemGroupUUID;
				}) !== -1) {
					return true;
				}
			});

		for (let i = 0; i < items.length; i++) {
			items[i].subRows = this.filterSubRows(items[i].subRows);
		}

		return items;
	}

	filterSubRows(subRowsArg) {
		const subRows = subRowsArg.filter((subRow) => {
			return subRow.matchesSearch;
		});

		for (let i = 0; i < subRows.length; i++) {
			if (subRows[i].subRows)  // has subRows, then filter
				subRows[i].subRows = this.filterSubRows(subRows[i].subRows);
		}
		return subRows;
	}

	filterEntity(entity) {
		let nameInSearch = (entity.searchValues.name || '').toLowerCase().includes(this.state.nameFilter.toLowerCase());
		let resolutionInSearch = (entity.searchValues.resolution || '').toLowerCase().includes(this.state.resolutionFilter.toLowerCase());
		let statusInSearch = (entity.searchValues.status || '').toLowerCase().includes(this.state.statusFilter.toLowerCase());
		let macAddressInSearch = (entity.searchValues.macAddress || '').toLowerCase().includes(this.state.macAddressFilter.toLowerCase());
		let orientationInSearch = (entity.searchValues.orientation || '').toLowerCase().includes(this.state.orientationFilter.toLowerCase());
		let directoryNumberInSearch = (entity.searchValues.directoryNumber || '').toLowerCase().includes(this.state.directoryNumberFilter.toLowerCase());
		let projectNumberInSearch = (entity.searchValues.projectNumber || '').toLowerCase().includes(this.state.nameFilter.toLowerCase());

		let isMatch = (nameInSearch || projectNumberInSearch) && resolutionInSearch && statusInSearch && macAddressInSearch && orientationInSearch && directoryNumberInSearch;

		if (isMatch) {
			entity.matchesSearch = true;
		} else {
			entity.matchesSearch = false;
		}

		entity.subRows.forEach((subEntity) => {
			this.filterEntity(subEntity);
		});

	}

	addParentToRows(rows, parent = false) {
		for (let row of rows) {
			if (parent) {
				row.parent = parent;
			}

			for (let subRow of row.subRows) {
				subRow.parent = row;
				this.addParentToRows(subRow.subRows, subRow);
			}
		}
	}

	makeSiblingsAvailable(rows) {
		for (let row of rows) {
			if (row.m1 === 'system') {
				if (row.parent.matchesSearch) {
					row.parent.subRows.forEach((subRow) => {
						subRow.matchesSearch = true;
					});
				}
			}
			this.makeSiblingsAvailable(row.subRows);
		}
	}

	addMatchingToRows(rows) {
		for (let row of rows) {
			this.addMatchingToRows(row.subRows);

			if (row.matchesSearch) {
				while (typeof row.parent !== 'undefined') {
					row.parent.matchesSearch = true;
					row = row.parent;
				}
			} else {
				/* let i = rows.indexOf(row);
				rows.splice(i, 1);  */
			}
		}
	}

	getEarliestExpiration() {
		let today = new Date();
		let prevDate = this.state.systemsAboutToExpire[0] || today;
		for (let i = 0; i < this.state.systemsAboutToExpire.length; i++) {
			if (this.state.systemsAboutToExpire[i] < prevDate) {
				prevDate = this.state.systemsAboutToExpire[i];
			}
		}
		let diffTime = 0;
		if (prevDate <= today) {
			diffTime = Math.abs(prevDate - today);
		} else {
			diffTime = Math.abs(today - prevDate);
		}
		let diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

		if (today > prevDate) {
			diffDays = Math.ceil(-diffTime / (1000 * 60 * 60 * 24));
		} else {
			diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		}

		return diffDays;
	}

	getSearchBars() {
		return (
			<div className={style.table}>
				<div className={style.thead}>
					<div className={style.tr}>
						<div className={style.th} style={this.state.systemNameColumn}>
							System Name
						</div>
						<div className={style.th} style={this.state.statusColumn}>
							Status
						</div>
						<div className={style.th} style={this.state.resolutionColumn}>
							Resolution
						</div>
						<div className={style.th} style={this.state.orientationColumn}>
							Orientation
						</div>
						<div className={style.th} style={this.state.macAddressColumn}>
							MAC Address
						</div>
						<div className={style.th} style={this.state.directoryNumberColumn}>
							Dir Num
						</div>
					</div>
				</div>
				<div className={style.systemsFilter}>
					<div className={style.td} style={this.state.systemNameColumn}>
						<input
							className="filter"
							type="text"
							placeholder="Filter..."
							data-testing-info={buildTestData('input--systemName')}
							onChange={(e) => {
								e.preventDefault();
								this.updateSearch('nameFilter', e.target.value);
							}}
							value={this.state.nameFilter}
						/>
					</div>
					<div className={style.td} style={this.state.statusColumn}>
						<input
							className="filter"
							type="text"
							placeholder="Filter..."
							data-testing-info={buildTestData('input--status')}
							onChange={(e) => {
								e.preventDefault();
								this.updateSearch('statusFilter', e.target.value);
							}}
							value={this.state.statusFilter}
						/>
					</div>
					<div className={style.td} style={this.state.resolutionColumn}>

					</div>

					<div className={style.td} style={this.state.orientationColumn}>

					</div>
					<div className={style.td} style={this.state.macAddressColumn}>
						<input
							className="filter"
							type="text"
							placeholder="Filter..."
							data-testing-info={buildTestData('input--macAddress')}
							onChange={(e) => {
								e.preventDefault();
								this.updateSearch('macAddressFilter', e.target.value);
							}}
							value={this.state.macAddressFilter}
						/>
					</div>
					<div className={style.td} style={this.state.directoryNumberColumn}>
						<input
							className="filter"
							type="text"
							placeholder="Filter..."
							data-testing-info={buildTestData('input--directoryNumber')}
							onChange={(e) => {
								e.preventDefault();
								this.updateSearch('directoryNumberFilter', e.target.value);
							}}
							value={this.state.directoryNumberFilter}
						/>
					</div>
				</div>
			</div>
		);
	}

	expandAllRows() {
		let rowsToExpand = this.getFilteredData();

		this.setState({
			systemRows: rowsToExpand.map((element, index) => {
				return {
					index: true,
				};
			}),
		});
	}


	collapseAllRows() {
		this.setState({
			systemRows: [],
		});
	}

	render() {
		let newGroup = null;

		// No need to check isInitialized() - App.js ensures permissions are ready before rendering

		const {
			systemRows,
		} = this.state;
		if (hasGlobalScope()) {
			newGroup = (
				<div className='secondaryNav'>
					<div className='button' data-testing-info={buildTestData('button--newGroup')} onClick={((e) => {
						this.props.app.openModal({
							modalSize:  2,
							showModal:  true,
							modalProps: {
								title:          'New Hierarchy',
								pendingChanges: false,
								jsx:            (
									<SystemsModal
										app={this.props.app}
										accountUUID={this.props.accountUUID}
										fd={this.props.fd}
										settingMap={this.props.settingMap}
										isEdit={false}
										save={this.submitAndUpdateChanges.bind(this)}
										checkDataValidity={this.checkDataValidity.bind(this)}
									/>
								),
							},
						});
					})}>
						<CustomIcon icon='folder-plus' />New Hierarchy
					</div>

				</div>
			);
		}

		let searchBars = this.getSearchBars();
		return (
			<div className={`systems-page`} style={{ overflowX: 'hidden' }}>
				{this.renderImportModal()}
				<div className='settings-header' style={{ paddingBottom: '1.5em' }}>
					<h2>Hierarchy</h2>
				</div>
				{newGroup}
				<div className={style.systemsSearchHeader}>
					{searchBars}
				</div>
				{this.state.systemsTableData.length > 0 ?
					<div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
						<SystemsHierarchyTable
							array={this.getFilteredData()}
							openSystemsDetailModal={this.openSystemsDetailModal.bind(this)}
							defaultExpanded={systemRows}
							expandAllRows={this.expandAllRows.bind(this)}
							collapseAllRows={this.collapseAllRows.bind(this)}
							onMoveSystem={this.handleMoveSystem.bind(this)}
							onMoveSystemGroup={this.handleMoveSystemGroup.bind(this)}
							accountUUID={this.props.accountUUID}
							openConfirmModal={this.openMoveConfirmModal.bind(this)}
						/>
					</div> :
					<p>Create a New Hierarchy to begin adding systems.</p>
				}
			</div>
		);
	}
}

export default SystemsView;